// HIPAA-13 — Access Control (§ 164.312(a))
// ONE single card: Unique User ID, Emergency Access, Session Timeout, Encryption, Audit

import React, { useState } from "react";
import { Plus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// ── Types ─────────────────────────────────────────────────────────────────────
type AcSummary = {
  totalPolicies: number; activePolicies: number;
  mfaEnabled: boolean; encryptionAtRest: boolean; encryptionInTransit: boolean;
  avgSessionTimeout: number; policiesReviewOverdue: number;
  totalEmergencyAccess: number; pendingRequests: number; activeAccess: number;
  deniedRequests: number; revokedAccess: number; expiredAccess: number;
  ephiAccessed: number; pendingPostReview: number;
};
type AcPolicy = {
  id: string; policyName: string; description?: string; status: string; isActive: boolean;
  uniqueUserIdRequired: boolean; uniqueUserIdMethod?: string;
  sharedAccountsProhibited: boolean; userIdAuditLoggingEnabled: boolean; userIdReuseProhibited: boolean;
  primaryAuthMethod?: string; multiFactorAuthRequired: boolean; mfaMethod?: string;
  passwordComplexityRequired: boolean; passwordMinLength?: number; passwordExpiryDays?: number;
  accountLockoutEnabled: boolean; accountLockoutThreshold?: number;
  autoLogoffEnabled: boolean; sessionTimeoutMinutes: number; idleTimeoutMinutes?: number;
  forceLogoutOnInactivity: boolean; logoffScope?: string; sessionTimeoutEvidenceRef?: string;
  encryptionAtRestEnabled: boolean; encryptionAtRestMethod?: string; encryptionAtRestScope?: string;
  encryptionAtRestAudited: boolean; encryptionAtRestEvidenceRef?: string;
  encryptionInTransitEnabled: boolean; encryptionInTransitMethod?: string;
  insecureProtocolsBlocked: boolean; certificateExpiryDate?: string;
  encryptionInTransitAudited: boolean; encryptionInTransitEvidenceRef?: string;
  reviewFrequency?: string; lastReviewDate?: string; nextReviewDate?: string;
  evidenceReference?: string; complianceNotes?: string;
  createdAt: string; updatedAt: string;
};
type EmergencyAccess = {
  id: string; requestedBy: string; requestedByName?: string;
  requestedByRole?: string; requestedByDepartment?: string;
  requestedAt: string; accessType: string; urgencyLevel: string;
  justificationText: string; resourcesRequested?: string; status: string;
  approvedBy?: string; approvedByName?: string; approvalDate?: string;
  expiresAt?: string; approvalNotes?: string;
  deniedBy?: string; denialDate?: string; denialReason?: string;
  revokedBy?: string; revokedAt?: string; revocationReason?: string;
  accessStartedAt?: string; accessedResources?: string;
  ephiAccessed: boolean; ephiAccessSummary?: string;
  postAccessReviewRequired: boolean; postAccessReviewComplete: boolean;
  postAccessReviewDate?: string; postAccessReviewedBy?: string;
  isExpired: boolean; isActive: boolean;
  notes?: string; createdAt: string; updatedAt: string;
};

const EA_ACCESS_TYPES = ["BreakGlass","EmergencyAdmin","SystemRecovery","DataRecovery","ClinicalEmergency","Other"];
const EA_URGENCIES    = ["Low","Medium","High","Critical"];
const ACP_STATUSES    = ["Active","Draft","UnderReview","Archived"];
const REVIEW_FREQS    = ["Monthly","Quarterly","SemiAnnual","Annual","AsNeeded"];
const AUTH_METHODS    = ["Password","MFA","SSO","Certificate","Biometric","Other"];
const ENC_METHODS     = ["AES-256","AES-128","RSA-2048","TLS-1.2","TLS-1.3","IPSec","Other"];

function eaStatusVariant(status: string): "default"|"secondary"|"destructive"|"outline" {
  if (status === "Approved") return "default";
  if (status === "Denied" || status === "Expired") return "destructive";
  if (status === "Revoked") return "secondary";
  return "outline";
}
function urgencyVariant(u: string): "default"|"secondary"|"destructive"|"outline" {
  if (u === "Critical") return "destructive";
  if (u === "High") return "outline";
  return "secondary";
}
function fmtDate(d?: string) { return d ? new Date(d).toLocaleString() : "—"; }
function fmtDateOnly(d?: string) { return d ? new Date(d).toLocaleDateString() : "—"; }

// ── Main Component ─────────────────────────────────────────────────────────────
export function HipaaAccessControlSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string, string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
  const BASE       = "/api/organization/compliance/hipaa/access-control";
  const qc         = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["ac13-summary"] });
    qc.invalidateQueries({ queryKey: ["ac13-policies"] });
    qc.invalidateQueries({ queryKey: ["ac13-emergency"] });
  };

  // Tab state
  const [tab, setTab] = useState<"overview"|"policies"|"emergency">("overview");

  // ── Summary ──────────────────────────────────────────────────────────────────
  const summaryQ = useQuery<AcSummary>({
    queryKey: ["ac13-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── Policies ─────────────────────────────────────────────────────────────────
  const [policyPage, setPolicyPage] = useState(1);
  const [policyStatus, setPolicyStatus] = useState("");
  const policiesQ = useQuery<{ items: AcPolicy[]; total: number }>({
    queryKey: ["ac13-policies", policyPage, policyStatus],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(policyPage), pageSize: "10" });
      if (policyStatus) p.set("status", policyStatus);
      const r = await authFetch(`${BASE}/policies?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // Create policy form
  const defPolicyForm = () => ({
    policyName: "", description: "", status: "Active",
    uniqueUserIdRequired: true, uniqueUserIdMethod: "username",
    sharedAccountsProhibited: true, userIdAuditLoggingEnabled: true, userIdReuseProhibited: true,
    primaryAuthMethod: "Password", multiFactorAuthRequired: false, mfaMethod: "",
    passwordComplexityRequired: true, passwordMinLength: "8", passwordExpiryDays: "90",
    accountLockoutEnabled: true, accountLockoutThreshold: "5",
    autoLogoffEnabled: true, sessionTimeoutMinutes: "15", idleTimeoutMinutes: "15",
    forceLogoutOnInactivity: true, logoffScope: "All", sessionTimeoutEvidenceRef: "",
    encryptionAtRestEnabled: false, encryptionAtRestMethod: "AES-256", encryptionAtRestScope: "", encryptionAtRestEvidenceRef: "",
    encryptionInTransitEnabled: false, encryptionInTransitMethod: "TLS-1.3", insecureProtocolsBlocked: false, encryptionInTransitEvidenceRef: "",
    reviewFrequency: "Annual", nextReviewDate: "", evidenceReference: "", complianceNotes: "",
  });
  const [showCreatePolicy, setShowCreatePolicy] = useState(false);
  const [pForm, setPForm] = useState(defPolicyForm());
  const createPolicyMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/policies`, {
        method: "POST",
        body: JSON.stringify({
          ...pForm,
          passwordMinLength: pForm.passwordMinLength ? parseInt(pForm.passwordMinLength) : null,
          passwordExpiryDays: pForm.passwordExpiryDays ? parseInt(pForm.passwordExpiryDays) : null,
          accountLockoutThreshold: pForm.accountLockoutThreshold ? parseInt(pForm.accountLockoutThreshold) : null,
          sessionTimeoutMinutes: parseInt(pForm.sessionTimeoutMinutes),
          idleTimeoutMinutes: pForm.idleTimeoutMinutes ? parseInt(pForm.idleTimeoutMinutes) : null,
          nextReviewDate: pForm.nextReviewDate || null,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Failed"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Access control policy created." }); setShowCreatePolicy(false); setPForm(defPolicyForm()); invalidate(); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Edit policy
  const [editPolicy, setEditPolicy] = useState<AcPolicy | null>(null);
  const [eForm, setEForm] = useState<any>({});
  const updatePolicyMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/policies/${editPolicy!.id}`, {
        method: "PUT",
        body: JSON.stringify({
          ...eForm,
          sessionTimeoutMinutes: eForm.sessionTimeoutMinutes ? parseInt(String(eForm.sessionTimeoutMinutes)) : undefined,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Failed"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Policy updated." }); setEditPolicy(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
  const deletePolicyMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/policies/${id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Delete failed");
    },
    onSuccess: () => { toast({ title: "Policy deleted." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // ── Emergency Access ─────────────────────────────────────────────────────────
  const [eaPage, setEaPage] = useState(1);
  const [eaStatus, setEaStatus] = useState("");
  const [eaType, setEaType] = useState("");
  const [eaPending, setEaPending] = useState(false);
  const emergencyQ = useQuery<{ items: EmergencyAccess[]; total: number }>({
    queryKey: ["ac13-emergency", eaPage, eaStatus, eaType, eaPending],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(eaPage), pageSize: "10" });
      if (eaStatus) p.set("status", eaStatus);
      if (eaType) p.set("accessType", eaType);
      if (eaPending) p.set("pendingReview", "true");
      const r = await authFetch(`${BASE}/emergency?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // Request form
  const defReqForm = () => ({
    requestedBy: "", requestedByName: "", requestedByRole: "", requestedByDepartment: "",
    accessType: "BreakGlass", urgencyLevel: "High", justificationText: "",
    resourcesRequested: "", patientContext: "", incidentReference: "",
  });
  const [showRequest, setShowRequest] = useState(false);
  const [reqForm, setReqForm] = useState(defReqForm());
  const requestMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/emergency`, { method: "POST", body: JSON.stringify(reqForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Failed"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Emergency access request submitted." }); setShowRequest(false); setReqForm(defReqForm()); invalidate(); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Approve modal
  const [approveId, setApproveId] = useState<string | null>(null);
  const [approveForm, setApproveForm] = useState({ approvedBy: "", approvedByName: "", expiresAt: "", approvalNotes: "" });
  const approveMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/emergency/${approveId}/approve`, {
        method: "POST",
        body: JSON.stringify({ ...approveForm, expiresAt: approveForm.expiresAt || null }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Failed"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Emergency access approved." }); setApproveId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Deny modal
  const [denyId, setDenyId] = useState<string | null>(null);
  const [denyForm, setDenyForm] = useState({ deniedBy: "", deniedByName: "", denialReason: "" });
  const denyMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/emergency/${denyId}/deny`, { method: "POST", body: JSON.stringify(denyForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Failed"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Emergency access denied." }); setDenyId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Revoke modal
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [revokeForm, setRevokeForm] = useState({ revokedBy: "", revokedByName: "", revocationReason: "", ephiAccessed: false, ephiAccessSummary: "", accessedResources: "" });
  const revokeMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/emergency/${revokeId}/revoke`, { method: "POST", body: JSON.stringify(revokeForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Failed"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Emergency access revoked." }); setRevokeId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Post-review modal
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewForm, setReviewForm] = useState({ reviewedBy: "", reviewNotes: "", accessedResources: "", ephiAccessSummary: "" });
  const reviewMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/emergency/${reviewId}/post-review`, { method: "POST", body: JSON.stringify(reviewForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Failed"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Post-access review completed." }); setReviewId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Start access
  const startMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/emergency/${id}/start`, { method: "POST", body: JSON.stringify({}) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Failed"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Access use recorded." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Delete EA
  const deleteEaMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/emergency/${id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Delete failed");
    },
    onSuccess: () => { toast({ title: "Record deleted." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const s = summaryQ.data;
  const eaTotalPages = Math.ceil((emergencyQ.data?.total ?? 0) / 10);
  const policyTotalPages = Math.ceil((policiesQ.data?.total ?? 0) / 10);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Access Control (§ 164.312(a))</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
      {/* Summary dashboard */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { label: "Active Policies", value: s.activePolicies },
            { label: "MFA Enforced", value: s.mfaEnabled ? "Yes" : "No", warn: !s.mfaEnabled },
            { label: "Encryption at Rest", value: s.encryptionAtRest ? "Yes" : "No", warn: !s.encryptionAtRest },
            { label: "Encryption in Transit", value: s.encryptionInTransit ? "Yes" : "No", warn: !s.encryptionInTransit },
            { label: "Avg Session Timeout", value: `${s.avgSessionTimeout}m` },
            { label: "Pending Requests", value: s.pendingRequests, warn: s.pendingRequests > 0 },
            { label: "Active Access", value: s.activeAccess, warn: s.activeAccess > 0 },
            { label: "Pending Post-Review", value: s.pendingPostReview, warn: s.pendingPostReview > 0 },
          ].map(c => (
            <div key={c.label} className={`border rounded p-2 text-center ${(c as any).warn ? "border-destructive/40 bg-destructive/5" : ""}`}>
              <div className={`text-xl font-bold ${(c as any).warn ? "text-destructive" : ""}`}>{c.value}</div>
              <div className="text-xs text-muted-foreground">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Alerts */}
      {s && s.pendingRequests > 0 && (
        <div className="border border-destructive/40 rounded p-3 bg-destructive/5 text-sm">
          <span className="font-medium text-destructive">{s.pendingRequests} emergency access request{s.pendingRequests !== 1 ? "s" : ""}</span>
          {" "}awaiting review. Emergency access is never silently approved.
        </div>
      )}
      {s && s.pendingPostReview > 0 && (
        <div className="border border-amber-400/40 rounded p-3 bg-amber-50/30 text-sm text-amber-800">
          {s.pendingPostReview} post-access review{s.pendingPostReview !== 1 ? "s" : ""} outstanding.
        </div>
      )}

      {/* Internal tabs */}
      <div className="flex gap-2 border-b pb-2">
        {(["overview","policies","emergency"] as const).map(t => (
          <button key={t} className={`text-sm font-medium px-3 py-1 rounded-t capitalize ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} onClick={() => setTab(t)}>
            {t === "overview" ? "Overview" : t === "policies" ? "Policies" : "Emergency Access"}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === "overview" && s && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Unique User ID */}
            <div className="border rounded p-3 space-y-1">
              <p className="font-medium text-sm">Unique User Identification</p>
              <p className="text-xs text-muted-foreground">§ 164.312(a)(2)(i)</p>
              <div className="flex flex-col gap-1 mt-2">
                <div className="flex justify-between text-sm"><span>Required</span><Badge variant={s.activePolicies > 0 ? "default" : "outline"}>Policy Set</Badge></div>
                <div className="flex justify-between text-sm"><span>Total Policies</span><span className="font-medium">{s.totalPolicies}</span></div>
                <div className="flex justify-between text-sm"><span>Active Policies</span><span className="font-medium">{s.activePolicies}</span></div>
                {s.policiesReviewOverdue > 0 && <Badge variant="destructive" className="w-fit">{s.policiesReviewOverdue} review(s) overdue</Badge>}
              </div>
            </div>
            {/* Session Timeout */}
            <div className="border rounded p-3 space-y-1">
              <p className="font-medium text-sm">Automatic Logoff</p>
              <p className="text-xs text-muted-foreground">§ 164.312(a)(2)(iii)</p>
              <div className="flex flex-col gap-1 mt-2">
                <div className="flex justify-between text-sm"><span>Avg Timeout</span><span className="font-medium">{s.avgSessionTimeout}m</span></div>
                <div className="flex justify-between text-sm"><span>MFA Enforced</span><Badge variant={s.mfaEnabled ? "default" : "destructive"}>{s.mfaEnabled ? "Yes" : "No"}</Badge></div>
              </div>
            </div>
            {/* Encryption */}
            <div className="border rounded p-3 space-y-1">
              <p className="font-medium text-sm">Encryption</p>
              <p className="text-xs text-muted-foreground">§ 164.312(a)(2)(iv) / § 164.312(e)(2)(ii)</p>
              <div className="flex flex-col gap-1 mt-2">
                <div className="flex justify-between text-sm"><span>At Rest</span><Badge variant={s.encryptionAtRest ? "default" : "destructive"}>{s.encryptionAtRest ? "Enabled" : "Not configured"}</Badge></div>
                <div className="flex justify-between text-sm"><span>In Transit</span><Badge variant={s.encryptionInTransit ? "default" : "destructive"}>{s.encryptionInTransit ? "Enabled" : "Not configured"}</Badge></div>
              </div>
            </div>
          </div>
          {/* Emergency access summary */}
          <div className="border rounded p-3">
            <p className="font-medium text-sm mb-2">Emergency Access Procedure (§ 164.312(a)(2)(ii))</p>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center text-sm">
              {[
                ["Total", s.totalEmergencyAccess],
                ["Pending", s.pendingRequests],
                ["Active", s.activeAccess],
                ["Denied", s.deniedRequests],
                ["Revoked", s.revokedAccess],
                ["ePHI Accessed", s.ephiAccessed],
              ].map(([label, value]) => (
                <div key={String(label)} className="border rounded p-1">
                  <div className="font-bold">{value}</div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Policies ── */}
      {tab === "policies" && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <select className="border rounded px-2 py-1 text-sm" value={policyStatus} onChange={e => { setPolicyStatus(e.target.value); setPolicyPage(1); }}>
              <option value="">All Statuses</option>
              {ACP_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {canManage && <Button size="sm" className="ml-auto bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => setShowCreatePolicy(true)}><Plus className="h-4 w-4" /> New Policy</Button>}
          </div>

          {/* Create form */}
          {showCreatePolicy && (
            <div className="border rounded p-4 space-y-4 bg-muted/30">
              <h4 className="font-medium">New Access Control Policy</h4>
              {/* Identity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="text-xs font-medium">Policy Name *</label><Input value={pForm.policyName} onChange={e => setPForm(f => ({ ...f, policyName: e.target.value }))} placeholder="HIPAA Access Control Policy 2026" /></div>
                <div><label className="text-xs font-medium">Status</label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={pForm.status} onChange={e => setPForm(f => ({ ...f, status: e.target.value }))}>
                    {ACP_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2"><label className="text-xs font-medium">Description</label><Input value={pForm.description} onChange={e => setPForm(f => ({ ...f, description: e.target.value }))} /></div>
              </div>
              {/* User ID */}
              <details open>
                <summary className="text-sm font-medium cursor-pointer">Unique User Identification</summary>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                  <div><label className="text-xs font-medium">User ID Method</label><Input value={pForm.uniqueUserIdMethod} onChange={e => setPForm(f => ({ ...f, uniqueUserIdMethod: e.target.value }))} placeholder="username / email" /></div>
                  <div><label className="text-xs font-medium">Primary Auth Method</label>
                    <select className="w-full border rounded px-2 py-1.5 text-sm" value={pForm.primaryAuthMethod} onChange={e => setPForm(f => ({ ...f, primaryAuthMethod: e.target.value }))}>
                      {AUTH_METHODS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div><label className="text-xs font-medium">MFA Method</label><Input value={pForm.mfaMethod} onChange={e => setPForm(f => ({ ...f, mfaMethod: e.target.value }))} placeholder="TOTP / SMS / Hardware" /></div>
                  <div><label className="text-xs font-medium">Password Min Length</label><Input type="number" value={pForm.passwordMinLength} onChange={e => setPForm(f => ({ ...f, passwordMinLength: e.target.value }))} /></div>
                  <div><label className="text-xs font-medium">Password Expiry (days)</label><Input type="number" value={pForm.passwordExpiryDays} onChange={e => setPForm(f => ({ ...f, passwordExpiryDays: e.target.value }))} /></div>
                  <div><label className="text-xs font-medium">Account Lockout Threshold</label><Input type="number" value={pForm.accountLockoutThreshold} onChange={e => setPForm(f => ({ ...f, accountLockoutThreshold: e.target.value }))} /></div>
                </div>
                <div className="flex flex-wrap gap-4 text-sm mt-2">
                  {([
                    ["uniqueUserIdRequired","Unique User ID Required"],
                    ["sharedAccountsProhibited","Shared Accounts Prohibited"],
                    ["userIdAuditLoggingEnabled","User ID Audit Logging"],
                    ["userIdReuseProhibited","User ID Reuse Prohibited"],
                    ["multiFactorAuthRequired","MFA Required"],
                    ["passwordComplexityRequired","Password Complexity Required"],
                    ["accountLockoutEnabled","Account Lockout Enabled"],
                  ] as [keyof typeof pForm, string][]).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={pForm[k] as boolean} onChange={e => setPForm(f => ({ ...f, [k]: e.target.checked }))} />
                      {label}
                    </label>
                  ))}
                </div>
              </details>
              {/* Session Timeout */}
              <details open>
                <summary className="text-sm font-medium cursor-pointer">Session Timeout (§ 164.312(a)(2)(iii))</summary>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                  <div><label className="text-xs font-medium">Session Timeout (minutes) *</label><Input type="number" value={pForm.sessionTimeoutMinutes} onChange={e => setPForm(f => ({ ...f, sessionTimeoutMinutes: e.target.value }))} /></div>
                  <div><label className="text-xs font-medium">Idle Timeout (minutes)</label><Input type="number" value={pForm.idleTimeoutMinutes} onChange={e => setPForm(f => ({ ...f, idleTimeoutMinutes: e.target.value }))} /></div>
                  <div><label className="text-xs font-medium">Logoff Scope</label>
                    <select className="w-full border rounded px-2 py-1.5 text-sm" value={pForm.logoffScope} onChange={e => setPForm(f => ({ ...f, logoffScope: e.target.value }))}>
                      {["All","Web","Desktop","Mobile"].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-3"><label className="text-xs font-medium">Session Timeout Evidence Reference</label><Input value={pForm.sessionTimeoutEvidenceRef} onChange={e => setPForm(f => ({ ...f, sessionTimeoutEvidenceRef: e.target.value }))} placeholder="CERT-SESSION-001" /></div>
                </div>
                <div className="flex gap-4 text-sm mt-2">
                  {([
                    ["autoLogoffEnabled","Auto Logoff Enabled"],
                    ["forceLogoutOnInactivity","Force Logout on Inactivity"],
                  ] as [keyof typeof pForm, string][]).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={pForm[k] as boolean} onChange={e => setPForm(f => ({ ...f, [k]: e.target.checked }))} />
                      {label}
                    </label>
                  ))}
                </div>
              </details>
              {/* Encryption */}
              <details>
                <summary className="text-sm font-medium cursor-pointer">Encryption (§ 164.312(a)(2)(iv) / § 164.312(e)(2)(ii))</summary>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                  <div><label className="text-xs font-medium">At Rest Method</label>
                    <select className="w-full border rounded px-2 py-1.5 text-sm" value={pForm.encryptionAtRestMethod} onChange={e => setPForm(f => ({ ...f, encryptionAtRestMethod: e.target.value }))}>
                      {ENC_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div><label className="text-xs font-medium">At Rest Scope</label><Input value={pForm.encryptionAtRestScope} onChange={e => setPForm(f => ({ ...f, encryptionAtRestScope: e.target.value }))} placeholder="Database / All" /></div>
                  <div><label className="text-xs font-medium">At Rest Evidence Ref</label><Input value={pForm.encryptionAtRestEvidenceRef} onChange={e => setPForm(f => ({ ...f, encryptionAtRestEvidenceRef: e.target.value }))} /></div>
                  <div><label className="text-xs font-medium">In Transit Method</label>
                    <select className="w-full border rounded px-2 py-1.5 text-sm" value={pForm.encryptionInTransitMethod} onChange={e => setPForm(f => ({ ...f, encryptionInTransitMethod: e.target.value }))}>
                      {ENC_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div><label className="text-xs font-medium">In Transit Evidence Ref</label><Input value={pForm.encryptionInTransitEvidenceRef} onChange={e => setPForm(f => ({ ...f, encryptionInTransitEvidenceRef: e.target.value }))} /></div>
                </div>
                <div className="flex gap-4 text-sm mt-2">
                  {([
                    ["encryptionAtRestEnabled","Encryption at Rest"],
                    ["encryptionInTransitEnabled","Encryption in Transit"],
                    ["insecureProtocolsBlocked","Insecure Protocols Blocked"],
                  ] as [keyof typeof pForm, string][]).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={pForm[k] as boolean} onChange={e => setPForm(f => ({ ...f, [k]: e.target.checked }))} />
                      {label}
                    </label>
                  ))}
                </div>
              </details>
              {/* Review */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div><label className="text-xs font-medium">Review Frequency</label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={pForm.reviewFrequency} onChange={e => setPForm(f => ({ ...f, reviewFrequency: e.target.value }))}>
                    {REVIEW_FREQS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={pForm.nextReviewDate} onChange={e => setPForm(f => ({ ...f, nextReviewDate: e.target.value }))} /></div>
                <div><label className="text-xs font-medium">Evidence Reference</label><Input value={pForm.evidenceReference} onChange={e => setPForm(f => ({ ...f, evidenceReference: e.target.value }))} /></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => createPolicyMut.mutate()} disabled={createPolicyMut.isPending}>{createPolicyMut.isPending ? "Saving…" : "Create Policy"}</Button>
                <Button size="sm" variant="outline" onClick={() => { setShowCreatePolicy(false); setPForm(defPolicyForm()); }}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Policy list */}
          {policiesQ.isLoading ? <Skeleton className="h-24 w-full" /> : (
            <div className="space-y-2">
              {(policiesQ.data?.items ?? []).map(p => (
                <div key={p.id} className="border rounded p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{p.policyName}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <Badge variant={p.status === "Active" ? "default" : "secondary"}>{p.status}</Badge>
                        {p.multiFactorAuthRequired && <Badge variant="default">MFA</Badge>}
                        {p.encryptionAtRestEnabled && <Badge variant="outline">Enc@Rest</Badge>}
                        {p.encryptionInTransitEnabled && <Badge variant="outline">Enc@Transit</Badge>}
                        <Badge variant="outline">Timeout: {p.sessionTimeoutMinutes}m</Badge>
                        {p.nextReviewDate && new Date(p.nextReviewDate) < new Date() && <Badge variant="destructive">Review Overdue</Badge>}
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => { setEditPolicy(p); setEForm({ sessionTimeoutMinutes: p.sessionTimeoutMinutes, status: p.status, reviewNotes: "" }); }}>Edit</Button>
                        <Button size="sm" variant="destructive" onClick={() => { if (confirm(`Delete "${p.policyName}"?`)) deletePolicyMut.mutate(p.id); }}>Del</Button>
                      </div>
                    )}
                  </div>
                  {/* Inline details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 text-xs text-muted-foreground">
                    <span>Auth: {p.primaryAuthMethod || "—"}</span>
                    <span>MFA: {p.mfaMethod || (p.multiFactorAuthRequired ? "Required" : "No")}</span>
                    <span>Lockout after: {p.accountLockoutThreshold ?? "—"} attempts</span>
                    <span>Review: {p.reviewFrequency || "—"}</span>
                    <span>Enc@Rest: {p.encryptionAtRestMethod || "—"}</span>
                    <span>Enc@Transit: {p.encryptionInTransitMethod || "—"}</span>
                    <span>Insecure blocked: {p.insecureProtocolsBlocked ? "Yes" : "No"}</span>
                    <span>Next review: {fmtDateOnly(p.nextReviewDate)}</span>
                  </div>
                </div>
              ))}
              {(policiesQ.data?.total ?? 0) === 0 && !policiesQ.isLoading && (
                <p className="text-sm text-muted-foreground text-center py-4">No access control policies yet.</p>
              )}
            </div>
          )}
          {policyTotalPages > 1 && (
            <div className="flex gap-2 justify-center">
              <Button size="sm" variant="outline" disabled={policyPage <= 1} onClick={() => setPolicyPage(p => p - 1)}>Prev</Button>
              <span className="text-sm py-1">{policyPage} / {policyTotalPages}</span>
              <Button size="sm" variant="outline" disabled={policyPage >= policyTotalPages} onClick={() => setPolicyPage(p => p + 1)}>Next</Button>
            </div>
          )}
        </div>
      )}

      {/* ── Emergency Access ── */}
      {tab === "emergency" && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <select className="border rounded px-2 py-1 text-sm" value={eaStatus} onChange={e => { setEaStatus(e.target.value); setEaPage(1); }}>
              <option value="">All Statuses</option>
              {["Pending","Approved","Denied","Revoked","Expired"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={eaType} onChange={e => { setEaType(e.target.value); setEaPage(1); }}>
              <option value="">All Types</option>
              {EA_ACCESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="flex items-center gap-1 text-sm cursor-pointer">
              <input type="checkbox" checked={eaPending} onChange={e => { setEaPending(e.target.checked); setEaPage(1); }} />
              Pending Post-Review
            </label>
            {canManage && <Button size="sm" className="ml-auto bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => setShowRequest(true)}><Plus className="h-4 w-4" /> Request Access</Button>}
          </div>

          {/* Request form */}
          {showRequest && (
            <div className="border rounded p-4 space-y-3 bg-muted/30">
              <h4 className="font-medium">Request Emergency Access</h4>
              <p className="text-xs text-muted-foreground">Emergency access requires documented justification and explicit approval. Requests are never auto-approved.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="text-xs font-medium">Requested By (User ID) *</label><Input value={reqForm.requestedBy} onChange={e => setReqForm(f => ({ ...f, requestedBy: e.target.value }))} /></div>
                <div><label className="text-xs font-medium">Requestor Name</label><Input value={reqForm.requestedByName} onChange={e => setReqForm(f => ({ ...f, requestedByName: e.target.value }))} /></div>
                <div><label className="text-xs font-medium">Role</label><Input value={reqForm.requestedByRole} onChange={e => setReqForm(f => ({ ...f, requestedByRole: e.target.value }))} /></div>
                <div><label className="text-xs font-medium">Department</label><Input value={reqForm.requestedByDepartment} onChange={e => setReqForm(f => ({ ...f, requestedByDepartment: e.target.value }))} /></div>
                <div><label className="text-xs font-medium">Access Type</label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={reqForm.accessType} onChange={e => setReqForm(f => ({ ...f, accessType: e.target.value }))}>
                    {EA_ACCESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><label className="text-xs font-medium">Urgency Level</label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={reqForm.urgencyLevel} onChange={e => setReqForm(f => ({ ...f, urgencyLevel: e.target.value }))}>
                    {EA_URGENCIES.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2"><label className="text-xs font-medium">Justification *</label><textarea className="w-full border rounded px-2 py-1.5 text-sm h-20 resize-y" value={reqForm.justificationText} onChange={e => setReqForm(f => ({ ...f, justificationText: e.target.value }))} placeholder="Document the clinical or operational emergency requiring this access…" /></div>
                <div><label className="text-xs font-medium">Resources Required</label><Input value={reqForm.resourcesRequested} onChange={e => setReqForm(f => ({ ...f, resourcesRequested: e.target.value }))} placeholder="System, database, or data required" /></div>
                <div><label className="text-xs font-medium">Incident Reference</label><Input value={reqForm.incidentReference} onChange={e => setReqForm(f => ({ ...f, incidentReference: e.target.value }))} /></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => requestMut.mutate()} disabled={requestMut.isPending}>{requestMut.isPending ? "Submitting…" : "Submit Request"}</Button>
                <Button size="sm" variant="outline" onClick={() => { setShowRequest(false); setReqForm(defReqForm()); }}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Emergency access list */}
          {emergencyQ.isLoading ? <Skeleton className="h-24 w-full" /> : (
            <div className="space-y-2">
              {(emergencyQ.data?.items ?? []).map(ea => (
                <div key={ea.id} className="border rounded p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{ea.requestedByName ?? ea.requestedBy} — {ea.accessType}</p>
                      <p className="text-xs text-muted-foreground">{ea.requestedByRole}{ea.requestedByDepartment ? ` / ${ea.requestedByDepartment}` : ""} · {fmtDate(ea.requestedAt)}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <Badge variant={eaStatusVariant(ea.status)}>{ea.status}</Badge>
                        <Badge variant={urgencyVariant(ea.urgencyLevel)}>{ea.urgencyLevel}</Badge>
                        {ea.ephiAccessed && <Badge variant="destructive">ePHI Accessed</Badge>}
                        {ea.postAccessReviewRequired && !ea.postAccessReviewComplete && <Badge variant="outline">Review Pending</Badge>}
                        {ea.expiresAt && <span className="text-xs text-muted-foreground">Expires: {fmtDate(ea.expiresAt)}</span>}
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex flex-wrap gap-1 flex-shrink-0">
                        {ea.status === "Pending" && <>
                          <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => { setApproveId(ea.id); setApproveForm({ approvedBy: "", approvedByName: "", expiresAt: "", approvalNotes: "" }); }}>Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => { setDenyId(ea.id); setDenyForm({ deniedBy: "", deniedByName: "", denialReason: "" }); }}>Deny</Button>
                        </>}
                        {ea.status === "Approved" && ea.isActive && <>
                          <Button size="sm" variant="outline" onClick={() => startMut.mutate(ea.id)}>Mark Started</Button>
                          <Button size="sm" variant="destructive" onClick={() => { setRevokeId(ea.id); setRevokeForm({ revokedBy: "", revokedByName: "", revocationReason: "", ephiAccessed: false, ephiAccessSummary: "", accessedResources: "" }); }}>Revoke</Button>
                        </>}
                        {ea.postAccessReviewRequired && !ea.postAccessReviewComplete && (
                          <Button size="sm" variant="outline" onClick={() => { setReviewId(ea.id); setReviewForm({ reviewedBy: "", reviewNotes: "", accessedResources: "", ephiAccessSummary: "" }); }}>Complete Review</Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => { if (confirm("Delete this record?")) deleteEaMut.mutate(ea.id); }}>Del</Button>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{ea.justificationText}</p>
                  {ea.approvalNotes && <p className="text-xs text-green-700">Approval note: {ea.approvalNotes}</p>}
                  {ea.denialReason && <p className="text-xs text-destructive">Denial reason: {ea.denialReason}</p>}
                  {ea.revocationReason && <p className="text-xs text-orange-700">Revocation: {ea.revocationReason}</p>}
                  {ea.postAccessReviewComplete && <p className="text-xs text-muted-foreground">Post-review by {ea.postAccessReviewedBy} on {fmtDateOnly(ea.postAccessReviewDate)}</p>}
                </div>
              ))}
              {(emergencyQ.data?.total ?? 0) === 0 && !emergencyQ.isLoading && (
                <p className="text-sm text-muted-foreground text-center py-4">No emergency access records.</p>
              )}
            </div>
          )}
          {eaTotalPages > 1 && (
            <div className="flex gap-2 justify-center">
              <Button size="sm" variant="outline" disabled={eaPage <= 1} onClick={() => setEaPage(p => p - 1)}>Prev</Button>
              <span className="text-sm py-1">{eaPage} / {eaTotalPages}</span>
              <Button size="sm" variant="outline" disabled={eaPage >= eaTotalPages} onClick={() => setEaPage(p => p + 1)}>Next</Button>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ── */}

      {/* Edit policy modal */}
      {editPolicy && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 max-w-md w-full">
            <h4 className="font-medium">Edit: {editPolicy.policyName}</h4>
            <div><label className="text-xs font-medium">Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={eForm.status ?? editPolicy.status} onChange={e => setEForm((f: any) => ({ ...f, status: e.target.value }))}>
                {ACP_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Session Timeout (minutes)</label><Input type="number" value={eForm.sessionTimeoutMinutes ?? editPolicy.sessionTimeoutMinutes} onChange={e => setEForm((f: any) => ({ ...f, sessionTimeoutMinutes: e.target.value }))} /></div>
            <div><label className="text-xs font-medium">MFA Required</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={eForm.multiFactorAuthRequired !== undefined ? String(eForm.multiFactorAuthRequired) : String(editPolicy.multiFactorAuthRequired)} onChange={e => setEForm((f: any) => ({ ...f, multiFactorAuthRequired: e.target.value === "true" }))}>
                <option value="true">Yes</option><option value="false">No</option>
              </select>
            </div>
            <div><label className="text-xs font-medium">Encryption at Rest</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={eForm.encryptionAtRestEnabled !== undefined ? String(eForm.encryptionAtRestEnabled) : String(editPolicy.encryptionAtRestEnabled)} onChange={e => setEForm((f: any) => ({ ...f, encryptionAtRestEnabled: e.target.value === "true" }))}>
                <option value="true">Enabled</option><option value="false">Disabled</option>
              </select>
            </div>
            <div><label className="text-xs font-medium">Encryption in Transit</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={eForm.encryptionInTransitEnabled !== undefined ? String(eForm.encryptionInTransitEnabled) : String(editPolicy.encryptionInTransitEnabled)} onChange={e => setEForm((f: any) => ({ ...f, encryptionInTransitEnabled: e.target.value === "true" }))}>
                <option value="true">Enabled</option><option value="false">Disabled</option>
              </select>
            </div>
            <div><label className="text-xs font-medium">Review Notes</label><Input value={eForm.reviewNotes ?? ""} onChange={e => setEForm((f: any) => ({ ...f, reviewNotes: e.target.value }))} /></div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => updatePolicyMut.mutate()} disabled={updatePolicyMut.isPending}>{updatePolicyMut.isPending ? "Saving…" : "Save"}</Button>
              <Button size="sm" variant="outline" onClick={() => setEditPolicy(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Approve modal */}
      {approveId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Approve Emergency Access</h4>
            <p className="text-xs text-muted-foreground">Set an expiry time — emergency access must have a defined end.</p>
            {[["approvedBy","Approved By (User ID) *"],["approvedByName","Approver Name"]].map(([k,label]) => (
              <div key={k}><label className="text-xs font-medium">{label}</label><Input value={(approveForm as any)[k]} onChange={e => setApproveForm(f => ({ ...f, [k]: e.target.value }))} /></div>
            ))}
            <div><label className="text-xs font-medium">Expires At *</label><Input type="datetime-local" value={approveForm.expiresAt} onChange={e => setApproveForm(f => ({ ...f, expiresAt: e.target.value }))} /></div>
            <div><label className="text-xs font-medium">Approval Notes</label><Input value={approveForm.approvalNotes} onChange={e => setApproveForm(f => ({ ...f, approvalNotes: e.target.value }))} /></div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>{approveMut.isPending ? "Approving…" : "Approve"}</Button>
              <Button size="sm" variant="outline" onClick={() => setApproveId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Deny modal */}
      {denyId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Deny Emergency Access</h4>
            {[["deniedBy","Denied By *"],["deniedByName","Denier Name"],["denialReason","Denial Reason *"]].map(([k,label]) => (
              <div key={k}><label className="text-xs font-medium">{label}</label><Input value={(denyForm as any)[k]} onChange={e => setDenyForm(f => ({ ...f, [k]: e.target.value }))} /></div>
            ))}
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" onClick={() => denyMut.mutate()} disabled={denyMut.isPending}>{denyMut.isPending ? "Denying…" : "Deny Access"}</Button>
              <Button size="sm" variant="outline" onClick={() => setDenyId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke modal */}
      {revokeId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Revoke Emergency Access</h4>
            {[["revokedBy","Revoked By *"],["revokedByName","Revoker Name"],["revocationReason","Revocation Reason *"],["accessedResources","Resources Accessed"],["ephiAccessSummary","ePHI Access Summary (de-identified)"]].map(([k,label]) => (
              <div key={k}><label className="text-xs font-medium">{label}</label><Input value={(revokeForm as any)[k]} onChange={e => setRevokeForm(f => ({ ...f, [k]: e.target.value }))} /></div>
            ))}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={revokeForm.ephiAccessed} onChange={e => setRevokeForm(f => ({ ...f, ephiAccessed: e.target.checked }))} />
              ePHI was accessed (requires post-access review)
            </label>
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" onClick={() => revokeMut.mutate()} disabled={revokeMut.isPending}>{revokeMut.isPending ? "Revoking…" : "Revoke"}</Button>
              <Button size="sm" variant="outline" onClick={() => setRevokeId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Post-review modal */}
      {reviewId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Complete Post-Access Review</h4>
            {[["reviewedBy","Reviewed By *"],["reviewNotes","Review Notes"],["accessedResources","Resources Actually Accessed"],["ephiAccessSummary","ePHI Access Summary (de-identified, no raw PHI)"]].map(([k,label]) => (
              <div key={k}><label className="text-xs font-medium">{label}</label><Input value={(reviewForm as any)[k]} onChange={e => setReviewForm(f => ({ ...f, [k]: e.target.value }))} /></div>
            ))}
            <div className="flex gap-2">
              <Button size="sm" onClick={() => reviewMut.mutate()} disabled={reviewMut.isPending}>{reviewMut.isPending ? "Saving…" : "Complete Review"}</Button>
              <Button size="sm" variant="outline" onClick={() => setReviewId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
      </CardContent>
    </Card>
  );
}
